import * as O from 'fp-ts/lib/Option';
import {Button, MenuItem, useMediaQuery} from '@material-ui/core';
import AccountRouteConstants from 'Agent/Accounts/AccountRouteConstants';
import {
  changeAccountFilters,
  clearAccounts,
  FilterEnum,
  getAccounts,
  changeSort,
  clearSort,
} from 'Agent/Accounts/ducks/account';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import {theme} from 'Core/components/WithTheme';
import {ModalState, showModal} from 'Core/ducks/modal';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useLocation, useHistory} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import Column from 'Shared/components/PagedView/Column';
import {
  withDateFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {prop} from 'Shared/utilities/prop';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {toSentenceCase} from 'Shared/utilities/stringFormatHelpers';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {parseQueryString} from 'Shared/utilities/parsers';

const AccountStatuses: FilterEnum[] = [
  {
    name: 'Inactive',
    value: 'Inactive',
  },
  {
    name: 'Active',
    value: 'Active',
  },
];

const PaymentStatuses: FilterEnum[] = [
  {
    name: 'Delinquent',
    value: '1',
  },
  {
    name: 'Discrepant',
    value: '2',
  },
  {
    name: 'Pending',
    value: '3',
  },
  {
    name: 'Current',
    value: '4',
  },
];

type AccountsProps = {
  selectedAgency: AgencyFilters;
};

export const Accounts = (props: AccountsProps) => {
  const {search} = useLocation();
  const getQueryStringValue = parseQueryString(search);
  const {accounts, sort, filters: accountFilters} = useSelector((state: ReducerState) => state.account);
  const {currentAgentNumbers} = useSelector((state: ReducerState) => state.user.profile);
  const accountNameQuerystringValue = getQueryStringValue('accountName');
  const accountNumberQuerystringValue = getQueryStringValue('accountNumber');
  const accountName = O.getOrElse(() => accountFilters.name)(accountNameQuerystringValue);
  const accountNumber = O.getOrElse(() => accountFilters.number)(accountNumberQuerystringValue);

  const scopes = useSelector(selectUserScopes);
  const history = useHistory();
  const {selectedAgency} = props;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      changeAccountFilters({
        ...accountFilters,
        name: accountName,
        number: accountNumber,
      })
    );
  }, [accountName, accountNumber]);

  useEffect(() => {
    dispatch(
      getAccounts(
        currentAgentNumbers,
        selectedAgency.isNewYork,
        selectedAgency.encryptedIdentifier,
        undefined,
        accountFilters,
        O.toUndefined(sort)
      )
    );
  }, [currentAgentNumbers, selectedAgency.isNewYork, selectedAgency.encryptedIdentifier, accountFilters, sort]);

  useEffect(() => {
    return () => {
      dispatch(clearAccounts());
      dispatch(clearSort());
    };
  }, [clearAccounts, changeAccountFilters]);

  const changeFilter = withValueChangedHook(accountFilters, changeAccountFilters, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: accountFilters,
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'FilteredAccounts',
    relativeFileLocation: `/accounts/csv${addFilterParameters({
      ...accountFilters,
      agentNumbers: currentAgentNumbers.map(prop('value')),
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
    })}`,
    children: 'Export To CSV',
  };

  return (
    <PagedView
      pagedViewKey="accounts"
      data={accounts}
      sort={O.toUndefined(sort)}
      onChangeSort={sort => dispatch(changeSort(sort))}
      loadMore={page =>
        dispatch(
          getAccounts(
            currentAgentNumbers,
            selectedAgency.isNewYork,
            selectedAgency.encryptedIdentifier,
            page,
            accountFilters,
            O.toUndefined(sort)
          )
        )
      }
      actions={
        isMobile ? (
          <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
        ) : (
          <FileDownloadButton variant="contained" color="primary" displayTooltip {...exportToCsvProps} />
        )
      }
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="Account Number"
            sortColumnKey="number"
            filterProperties={withStringFilterProperties(filterProperties, 'number')}
            default
          >
            {account => account.number}
          </Column>
          <Column
            {...columnProps}
            title="Name"
            sortColumnKey="name"
            filterProperties={withStringFilterProperties(filterProperties, 'name')}
            default
          >
            {account => account.name}
          </Column>
          <Column
            {...columnProps}
            title="Agent of Record"
            sortColumnKey="agentOfRecordNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'agentOfRecordNumber')}
            default
          >
            {account => account.agentOfRecordNumber}
          </Column>
          <Column
            {...columnProps}
            title="Servicing Agent"
            sortColumnKey="servicingAgentNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'servicingAgentNumber')}
            default
          >
            {account => account.servicingAgentNumber}
          </Column>
          <Column
            {...columnProps}
            title="Submitted Date"
            sortColumnKey="lastAppSubmittedDate"
            filterProperties={withDateFilterProperties(filterProperties, 'lastAppSubmit')}
            default
          >
            {account => toFormattedDateAsUTC(account.lastAppSubmittedDate)}
          </Column>
          <Column
            {...columnProps}
            title="Payment Status"
            filterProperties={withSelectFilterProperties(filterProperties, 'paymentStatuses', PaymentStatuses)}
            default
          >
            {account => account.paymentStatus![0].name}
          </Column>
          <Column
            {...columnProps}
            title="Status"
            sortColumnKey="status"
            filterProperties={withSelectFilterProperties(filterProperties, 'status', AccountStatuses)}
            default
          >
            {account => toSentenceCase(account.status || '')}
          </Column>
          <Column {...columnProps} title="Annualized Premium">
            {account => toMoneyValue(account.annualizedPremium)}
          </Column>
          <Column {...columnProps} isActions title="" default>
            {account => (
              <>
                <RequireScope scopes={scopes} scope={Scope.ViewAccountDetails}>
                  <Link to={AccountRouteConstants.ACCOUNT_DETAILS(account.id!, account.number!)}>
                    <Button
                      id={`accounts-view-details-btn-${account.id}`}
                      className="primary-action-button"
                      color="primary"
                    >
                      View Details
                    </Button>
                  </Link>
                </RequireScope>
                <ActionsMenu id={`accounts-actions-menu-${account.id}`}>
                  {[
                    scopes.includes(Scope.SearchPolicies) && (
                      <MenuItem
                        id={`accounts-view-policies-btn-${account.id}`}
                        key={`view-policy-${account.number}`}
                        onClick={() => {
                          history.push(PolicyRouteConstants.POLICIES_BY_ACCOUNT(account.number!));
                        }}
                      >
                        View Policies
                      </MenuItem>
                    ),

                    scopes.includes(Scope.UploadEnrollmentApplication) && (
                      <MenuItem
                        id={`accounts-upload-enrollment-app-btn-${account.id}`}
                        key={`upload-enrollment-application-${account.number}`}
                        onClick={() =>
                          dispatch(
                            showModal(
                              ModalState.UploadEnrollmentApplication({
                                accountId: account.id!,
                                accountNumber: account.number!,
                                servicingAgentNumber: account.servicingAgentNumber,
                              })
                            )
                          )
                        }
                      >
                        Upload Enrollments
                      </MenuItem>
                    ),
                  ]}
                </ActionsMenu>
              </>
            )}
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default Accounts;
